import { RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import api from '../api/axiosInstance';
import { PermissionManager } from '../utils/PermissionManager';
import './css/unitPriceManagement.css';

export type UnitPrice = {
  id: number;
  brand: string;
  item: string;
  area: number;
  price: number;
};

export type PreviewPrice = UnitPrice & {
  originalPrice: number;
  newPrice: number;
};

function UnitPriceManagement() {
  // 아이템 고정 순서 상수
  const FIXED_ITEM_ORDER = [
    '발코니_단창_2W',
    '발코니_단창_3W',
    '발코니_이중창_2W',
    '발코니_이중창_3W',
    '일반_단창_2W',
    '일반_단창_3W',
    '일반_단창_4W',
    '일반_이중창_2W',
    '일반_이중창_3W',
    '일반_이중창_4W',
    '공틀_단창_2W',
    '공틀_단창_3W',
    '공틀_단창_4W',
    'FIX',
    '망추가(블랙스텐망)',

  ];

  const [unitPrices, setUnitPrices] = useState<UnitPrice[]>([]);
  const [filteredPrices, setFilteredPrices] = useState<UnitPrice[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // 브랜드 매핑 객체
  const brandMapping = {
    homeWindow: '홈윈도우',
    LX하우시스: 'LX하우시스',
    KCC홈씨씨: 'KCC홈씨씨',
  };

  // 브랜드 코드로 표시명 가져오기
  const getBrandDisplayName = (brandCode: string) => {
    return brandMapping[brandCode] || brandCode;
  };

  // 필터링 상태
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [searchArea, setSearchArea] = useState<string>('');

  // 브랜드/아이템 목록
  const [brands, setBrands] = useState<string[]>([]);
  const [items, setItems] = useState<string[]>([]);

  // 편집 모달 상태
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState<{
    id: number | null;
    brand: string;
    item: string;
    area: string;
    price: string;
  }>({
    id: null,
    brand: '',
    item: '',
    area: '',
    price: '',
  });

  // 새로 추가 모달 상태
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    brand: '',
    item: '',
    area: '',
    price: '',
    customItem: '',
  });

  // 에러 상태
  const [formError, setFormError] = useState({
    brand: '',
    item: '',
    area: '',
    price: '',
  });

  // 일괄 단가 수정 모달 상태
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false);
  const [bulkUpdateForm, setBulkUpdateForm] = useState<{
    brand: string;
    item: string;
    percentage: number | null;
    isIncrease: boolean;
  }>({
    brand: '',
    item: '',
    percentage: null,
    isIncrease: true,
  });
  const [previewData, setPreviewData] = useState<PreviewPrice[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [bulkItems, setBulkItems] = useState<string[]>([]); // 일괄 수정용 아이템 목록

  // 페이징 처리
  const [currentPage, setCurrentPage] = useState(1);
  
  // FIX 품목은 100개이므로 50-50으로 나누고, 나머지는 75-75로 나눔
  const isFIXItem = selectedItem === 'FIX';
  const rowsPerTable = isFIXItem ? 50 : 75;
  const rowsPerPage = rowsPerTable * 2; // 한 페이지에 표시할 총 개수

  // 페이징 처리
  const totalPages = Math.ceil(filteredPrices.length / rowsPerPage);
  const pageStart = (currentPage - 1) * rowsPerPage;
  const leftPrices = filteredPrices.slice(pageStart, pageStart + rowsPerTable);
  const rightPrices = filteredPrices.slice(
    pageStart + rowsPerTable,
    pageStart + rowsPerPage
  );

  // 전체 데이터 로드
  const loadUnitPrices = async (force = false) => {
    if (dataLoaded && !force) return; // 이미 로드되었으면 스킵 (강제 로드가 아닌 경우)

    setLoading(true);
    try {
      const res = await api.get('/api/admin/unit-prices');
      setUnitPrices(res.data);
      setDataLoaded(true);
    } catch (error) {
      console.error('Error loading unit prices:', error);
      alert('네트워크 오류가 발생했습니다.');
    }
    setLoading(false);
  };

  // 데이터 강제 새로고침
  const forceReloadData = async () => {
    setDataLoaded(false);
    setUnitPrices([]);
    setFilteredPrices([]);
    await loadUnitPrices(true); // 강제 로드
  };

  // 브랜드 목록 로드 (고정 순서)
  const loadBrands = async () => {
    try {
      const res = await api.get('/api/admin/brands');
      const data = res.data;
      const fixedOrder = ['homeWindow', 'LX하우시스', 'KCC홈씨씨'];
      const sortedBrands = fixedOrder.filter((brand) => data.includes(brand));
      const remainingBrands = data.filter(
        (brand) => !fixedOrder.includes(brand)
      );
      setBrands([...sortedBrands, ...remainingBrands]);
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  // 아이템 목록 로드 (고정 순서)
  const loadItems = async (brand: string) => {
    if (!brand) {
      setItems([]);
      return;
    }
    try {
      const res = await api.get(
        `/api/admin/brands/${encodeURIComponent(brand)}/items`
      );
      const data = res.data;
      const sortedItems = FIXED_ITEM_ORDER.filter((item) =>
        data.includes(item)
      );
      const remainingItems = data.filter(
        (item) => !FIXED_ITEM_ORDER.includes(item)
      );
      setItems([...sortedItems, ...remainingItems]);
    } catch (error) {
      console.error('Error loading items:', error);
    }
  };

  // 초기 로드
  useEffect(() => {
    loadBrands();
  }, []);

  // 브랜드 선택 시 데이터 로드
  useEffect(() => {
    if (selectedBrand) {
      loadUnitPrices(); // 전체 데이터 로드 후 필터링으로 처리
    } else {
      setUnitPrices([]);
      setFilteredPrices([]);
    }
  }, [selectedBrand]);

  // 브랜드, 아이템, 자평 필터링 로직
  useEffect(() => {
    let filtered = unitPrices;

    // 브랜드 필터링 (추가 보안)
    if (selectedBrand) {
      filtered = filtered.filter((price) => price.brand === selectedBrand);
    }

    // 아이템 필터링
    if (selectedItem) {
      filtered = filtered.filter((price) => price.item === selectedItem);
    }

    if (searchArea) {
      const areaNum = parseInt(searchArea);
      if (!isNaN(areaNum)) {
        filtered = filtered.filter((price) => price.area === areaNum);
      }
    }

    setFilteredPrices(filtered);
  }, [unitPrices, selectedBrand, selectedItem, searchArea]);

  // 브랜드 변경 시 아이템 로드
  useEffect(() => {
    if (selectedBrand) {
      loadItems(selectedBrand);
      setSelectedItem(''); // 아이템 선택 초기화
      setSearchArea(''); // 자평 검색 초기화
    } else {
      setItems([]);
      setSelectedItem('');
      setSearchArea('');
    }
  }, [selectedBrand]);

  // 폼 유효성 검사
  const validateForm = (form: {
    brand: string;
    item: string;
    area: string;
    price: string;
  }) => {
    let valid = true;
    let err = { brand: '', item: '', area: '', price: '' };

    if (!form.brand.trim()) {
      err.brand = '브랜드를 선택하세요.';
      valid = false;
    }

    if (!form.item.trim()) {
      err.item = '아이템을 입력하세요.';
      valid = false;
    }

    const area = parseInt(form.area);
    if (!form.area || isNaN(area) || area < 1 || area > 200) {
      err.area = '자평은 숫자를 입력하세요.';
      valid = false;
    }

    const price = parseInt(form.price);
    if (!form.price || isNaN(price) || price < 0) {
      err.price = '단가는 0 이상의 숫자를 입력하세요.';
      valid = false;
    }

    setFormError(err);
    return valid;
  };

  // 편집 모달 열기
  const handleEdit = (unitPrice: UnitPrice) => {
    setEditForm({
      id: unitPrice.id,
      brand: unitPrice.brand,
      item: unitPrice.item,
      area: unitPrice.area.toString(),
      price: unitPrice.price.toString(),
    });
    setEditModalOpen(true);
  };

  // 편집 저장 (단가만 수정)
  const handleSaveEdit = async () => {
    // 가격만 검증
    const price = parseInt(editForm.price);
    if (!editForm.price || isNaN(price) || price < 0) {
      setFormError({
        ...formError,
        price: '단가는 0 이상의 숫자를 입력하세요.',
      });
      return;
    }

    // 단가 검증 통과 시 오류 초기화
    setFormError({ ...formError, price: '' });

    try {
      const res = await api.put(`/api/admin/unit-prices/${editForm.id}`, {
        brand: editForm.brand,
        item: editForm.item,
        area: parseInt(editForm.area),
        price: parseInt(editForm.price),
      });

      if (res.data) {
        alert('단가 수정이 완료되었습니다.');
        setEditModalOpen(false);
        forceReloadData();
      } else {
        alert('수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error updating unit price:', error);
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  // 새로 추가
  const handleSaveAdd = async () => {
    // 직접입력일 때는 customItem 값을 사용
    const itemToSave =
      addForm.item === '직접입력' ? addForm.customItem : addForm.item;
    const formToValidate = { ...addForm, item: itemToSave };

    if (!validateForm(formToValidate)) return;

    try {
      const res = await api.post('/api/admin/unit-prices', {
        brand: addForm.brand,
        item: itemToSave,
        area: parseInt(addForm.area),
        price: parseInt(addForm.price),
      });

      if (res.data) {
        alert('추가가 완료되었습니다.');
        setAddModalOpen(false);
        setAddForm({
          brand: '',
          item: '',
          area: '',
          price: '',
          customItem: '',
        });
        forceReloadData();
      } else {
        alert('추가에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error adding unit price:', error);
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  // 삭제
  /*const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const response = await fetch(`/api/admin/unit-prices/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        alert('삭제가 완료되었습니다.');
        forceReloadData();
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error deleting unit price:', error);
      alert('네트워크 오류가 발생했습니다.');
    }
  };*/

  // 캐시 새로고침
  const handleRefreshCache = async () => {
    try {
      const res = await api.post('/api/admin/unit-prices/refresh');

      if (res.data) {
        alert('캐시가 새로고침되었습니다.');
        forceReloadData(); // 데이터도 새로고침
      } else {
        alert('캐시 새로고침에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error refreshing cache:', error);
      alert('네트워크 오류가 발생했습니다.');
    }
  };

  // 일괄 단가 수정 미리보기
  const handleBulkPreview = async () => {
    console.log('=== 일괄 수정 미리보기 시작 ===');
    console.log('bulkUpdateForm 상태:', bulkUpdateForm);
    console.log('증가 여부 (isIncrease):', bulkUpdateForm.isIncrease);
    console.log('퍼센트:', bulkUpdateForm.percentage);

    if (
      !bulkUpdateForm.brand ||
      bulkUpdateForm.percentage === null ||
      isNaN(bulkUpdateForm.percentage)
    ) {
      alert('브랜드와 퍼센트를 입력해주세요.');
      return;
    }

    const percentage = bulkUpdateForm.percentage;
    if (isNaN(percentage) || percentage < -100 || percentage > 100) {
      alert('-100~100 사이의 퍼센트를 입력해주세요.');
      return;
    }

    // 데이터가 로드되지 않았다면 먼저 로드
    let currentUnitPrices = unitPrices;
    if (!dataLoaded || unitPrices.length === 0) {
      setLoading(true);
      try {
        const res = await api.get('/api/admin/unit-prices');
        if (res.data) {
          currentUnitPrices = res.data;
          setUnitPrices(currentUnitPrices);
          setDataLoaded(true);
        }
      } catch (error) {
        console.error('Error loading unit prices:', error);
        alert('데이터를 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    // 필터링할 데이터 선택
    let targetData = currentUnitPrices.filter(
      (price) => price.brand === bulkUpdateForm.brand
    );
    console.log('브랜드 필터링 후 데이터 수:', targetData.length);

    // 특정 아이템이 선택된 경우만 필터링 (빈 값은 전체 아이템)
    if (bulkUpdateForm.item && bulkUpdateForm.item !== '') {
      targetData = targetData.filter(
        (price) => price.item === bulkUpdateForm.item
      );
      console.log('아이템 필터링 후 데이터 수:', targetData.length);
    }

    if (targetData.length === 0) {
      alert('수정할 데이터가 없습니다.');
      return;
    }

    // 단가 계산 (증가/감소)
    const multiplier = 1 + percentage / 100;
    console.log('계산 승수 (multiplier):', multiplier);
    console.log('계산 방식:', percentage > 0 ? '증가' : '감소');

    const preview = targetData.map((price) => ({
      ...price,
      originalPrice: price.price,
      newPrice: Math.round(price.price * multiplier),
    }));

    // 첫 번째 항목의 계산 과정 상세 로그
    if (preview.length > 0) {
      const first = preview[0];
      console.log('첫 번째 항목 계산 예시:');
      console.log('- 원본 단가:', first.originalPrice);
      console.log('- 승수:', multiplier);
      console.log('- 계산 결과:', first.originalPrice * multiplier);
      console.log('- 반올림 후:', first.newPrice);
    }

    console.log('미리보기 데이터:', preview.length, '개');
    console.log('========================');
    setPreviewData(preview);
    setShowPreview(true);
  };

  // 일괄 단가 수정 실행
  const handleBulkUpdate = async () => {
    if (previewData.length === 0) {
      alert('미리보기를 먼저 확인해주세요.');
      return;
    }

    if (
      !window.confirm(`${previewData.length}개 항목의 단가를 수정하시겠습니까?`)
    ) {
      return;
    }

    setLoading(true);
    try {
      const updateData = previewData.map((item) => ({
        id: item.id,
        price: item.newPrice,
      }));

      const res = await api.put(
        '/api/admin/unit-prices-bulk-update',
        updateData
      );

      if (res.data) {
        alert('일괄 단가 수정이 완료되었습니다.');
        setBulkUpdateModalOpen(false);
        setBulkUpdateForm({
          brand: '',
          item: '',
          percentage: null,
          isIncrease: true,
        });
        setPreviewData([]);
        setShowPreview(false);
        forceReloadData();
      } else {
        alert('일괄 수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error bulk updating prices:', error);
      alert('네트워크 오류가 발생했습니다.');
    }
    setLoading(false);
  };

  // 일괄 수정 모달 열기
  const handleOpenBulkUpdate = () => {
    setBulkUpdateModalOpen(true);
  };

  // 일괄 수정 모달 닫기
  const handleCloseBulkUpdate = () => {
    setBulkUpdateModalOpen(false);
    setBulkUpdateForm({
      brand: '',
      item: '',
      percentage: null,
      isIncrease: true,
    });
    setPreviewData([]);
    setShowPreview(false);
  };

  // 일괄 수정용 브랜드별 아이템 로드 (고정 순서)
  const loadBulkItems = async (brand: string) => {
    if (!brand) {
      setBulkItems([]);
      return;
    }

    // 데이터가 로드되지 않았다면 먼저 로드
    let currentUnitPrices = unitPrices;
    if (!dataLoaded || unitPrices.length === 0) {
      try {
        const res = await api.get('/api/admin/unit-prices');
        if (res.data) {
          currentUnitPrices = res.data;
          setUnitPrices(currentUnitPrices);
          setDataLoaded(true);
        }
      } catch (error) {
        console.error('Error loading unit prices:', error);
        setBulkItems([]);
        return;
      }
    }

    // unitPrices에서 해당 브랜드의 고유 아이템 목록 추출
    const uniqueItems = [
      ...new Set(
        currentUnitPrices
          .filter((price) => price.brand === brand)
          .map((price) => price.item)
      ),
    ];

    const sortedItems = FIXED_ITEM_ORDER.filter((item) =>
      uniqueItems.includes(item)
    );
    const remainingItems = uniqueItems.filter(
      (item) => !FIXED_ITEM_ORDER.includes(item)
    );

    setBulkItems([...sortedItems, ...remainingItems]);
  };

  // 퍼센트나 증가/감소 값 변경 시 자동으로 미리보기 업데이트
  useEffect(() => {
    // 미리보기가 이미 표시된 상태에서 퍼센트나 증가/감소가 변경되면 자동으로 미리보기 재계산
    if (
      showPreview &&
      bulkUpdateForm.brand &&
      bulkUpdateForm.percentage &&
      previewData.length > 0
    ) {
      const percentage = bulkUpdateForm.percentage;
      if (!isNaN(percentage) && percentage > -100 && percentage <= 100) {
        console.log('=== 자동 미리보기 업데이트 시작 ===');
        console.log('퍼센티지:', percentage);
        console.log('증가 여부:', percentage > 0);

        // 기존 previewData의 originalPrice를 사용해서 새로운 단가 계산
        const multiplier = 1 + percentage / 100;
        console.log('계산 승수:', multiplier);

        const updatedPreview = previewData.map((item) => ({
          ...item,
          newPrice: Math.round(item.originalPrice * multiplier),
        }));

        console.log('업데이트된 미리보기 데이터:', updatedPreview.length, '개');
        setPreviewData(updatedPreview);
      }
    }
  }, [bulkUpdateForm.percentage, bulkUpdateForm.isIncrease]);

  // useEffect로 자동 미리보기
  useEffect(() => {
    if (
      bulkUpdateForm.brand &&
      bulkUpdateForm.percentage &&
      !isNaN(bulkUpdateForm.percentage)
    ) {
      handleBulkPreview();
    } else {
      setPreviewData([]);
      setShowPreview(false);
    }
    // eslint-disable-next-line
  }, [
    bulkUpdateForm.brand,
    bulkUpdateForm.item,
    bulkUpdateForm.percentage,
    unitPrices,
  ]);

  return (
    <div>
      {/* 필터 및 액션 버튼 */}
      <div
        className="unit-price-header-row"
        style={{ justifyContent: 'flex-end', gap: '8px', fontSize: '13px' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <span
            className="unit-price-title"
            style={{
              fontSize: '24px',
              fontWeight: 700,
              letterSpacing: '-.5px',
              margin: 0,
              marginRight: '18px',
            }}>
            가격 관리
          </span>
        </div>
        <div
          className="unit-price-action-buttons"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <select
            value={selectedBrand}
            onChange={(e) => {
              setSelectedBrand(e.target.value);
              setCurrentPage(1);  // 필터 변경 시 1페이지로 이동
            }}
            className="unit-price-filter-select center-option"
            style={{
              height: '38px',
              fontSize: '15px',
              minWidth: '120px',
              borderRadius: '8px',
            }}>
            <option value="">브랜드 선택</option>
            {brands.map((brand) => (
              <option key={brand} value={brand}>
                {getBrandDisplayName(brand)}
              </option>
            ))}
          </select>
          <select
            value={selectedItem}
            onChange={(e) => {
              setSelectedItem(e.target.value);
              setCurrentPage(1);  // 필터 변경 시 1페이지로 이동
            }}
            className="unit-price-filter-select center-option"
            disabled={!selectedBrand}
            style={{
              height: '38px',
              fontSize: '15px',
              minWidth: '120px',
              borderRadius: '8px',
            }}>
            <option value="">전체 아이템</option>
            {(() => {
              // 아이템을 카테고리별로 분류
              const categorizeItems = (items: string[]) => {
                const categories = {
                  발코니: [
                    '발코니_단창_2W',
                    '발코니_단창_3W',
                    '발코니_이중창_2W',
                    '발코니_이중창_3W',
                  ],
                  일반: [
                    '일반_단창_2W',
                    '일반_단창_3W',
                    '일반_단창_4W',
                    '일반_이중창_2W',
                    '일반_이중창_3W',
                    '일반_이중창_4W',
                  ],
                  공틀: ['공틀_단창_2W', '공틀_단창_3W', '공틀_단창_4W'],
                  기타: ['FIX', '망추가(블랙스텐망)'],
                };
                const result: { [k: string]: string[] } = {
                  발코니: [],
                  일반: [],
                  공틀: [],
                  기타: [],
                  추가항목: [],
                };
                const allKnown = new Set([
                  ...categories.발코니,
                  ...categories.일반,
                  ...categories.공틀,
                  ...categories.기타,
                ]);
                Object.keys(categories).forEach((category) => {
                  categories[category].forEach((item) => {
                    if (items.includes(item)) {
                      result[category].push(item);
                    }
                  });
                });
                items.forEach((item) => {
                  if (!allKnown.has(item)) {
                    result.추가항목.push(item);
                  }
                });
                return result;
              };
              const categorized = categorizeItems(items);
              const elements: React.ReactElement[] = [];
              if (categorized.발코니.length > 0) {
                elements.push(
                  <optgroup key="발코니" label="─ 발코니 ─">
                    {categorized.발코니.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </optgroup>
                );
              }
              if (categorized.일반.length > 0) {
                elements.push(
                  <optgroup key="일반" label="─ 일반 ─">
                    {categorized.일반.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </optgroup>
                );
              }
              if (categorized.공틀.length > 0) {
                elements.push(
                  <optgroup key="공틀" label="─ 공틀 ─">
                    {categorized.공틀.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </optgroup>
                );
              }
              if (categorized.기타.length > 0) {
                elements.push(
                  <optgroup key="기타" label="─ 기타 ─">
                    {categorized.기타.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </optgroup>
                );
              }
              if (categorized.추가항목.length > 0) {
                elements.push(
                  <optgroup key="추가항목" label="─ 추가 항목 ─">
                    {categorized.추가항목.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </optgroup>
                );
              }
              return elements;
            })()}
          </select>
          {PermissionManager.canModifyPrices() && (
            <button
              className="unit-price-action-btn unit-price-bulk-update-btn apply-btn-center"
              onClick={handleOpenBulkUpdate}
              style={{
                height: '38px',
                fontSize: '15px',
                minWidth: '100px',
                borderRadius: '8px',
              }}>
              일괄 변경
            </button>
          )}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          margin: '8px 0 16px 0',
          fontSize: '13px',
        }}>
        <span>총 {filteredPrices.length}개 항목</span>
        {PermissionManager.canModifyPrices() && (
          <button
            onClick={handleRefreshCache}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              marginLeft: '6px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
            }}>
            <RefreshCw size={16} />
          </button>
        )}
      </div>

      {/* 필터링 */}
      {/*
      <div className="unit-price-filter-row">
        <div className="unit-price-filter-item">
          <label className="unit-price-filter-label">브랜드</label>
          <select
            value={selectedBrand}
            onChange={e => {
              setSelectedBrand(e.target.value);
              setCurrentPage(1);  // 필터 변경 시 1페이지로 이동
            }}
            className="unit-price-filter-select"
          >
            <option value="">브랜드 선택</option>
            {brands.map(brand => (
              <option key={brand} value={brand}>{getBrandDisplayName(brand)}</option>
            ))}
          </select>
        </div>

        <div className="unit-price-filter-item">
          <label className="unit-price-filter-label">아이템</label>
          <select
            value={selectedItem}
            onChange={e => {
              setSelectedItem(e.target.value);
              setCurrentPage(1);  // 필터 변경 시 1페이지로 이동
            }}
            className="unit-price-filter-select"
            disabled={!selectedBrand}
          >
            <option value="">전체</option>
            {(() => {
              // 아이템을 카테고리별로 분류
              const categorizeItems = (items) => {
                const categories = {
                  발코니: ['발코니_단창_2W', '발코니_단창_3W', '발코니_이중창_2W', '발코니_이중창_3W'],
                  일반: ['일반_단창_2W', '일반_단창_3W', '일반_단창_4W', '일반_이중창_2W', '일반_이중창_3W', '일반_이중창_4W'],
                  공틀: ['공틀_단창_2W', '공틀_단창_3W', '공틀_단창_4W'],
                  기타: ['FIX', '망추가(블랙스텐망)']
                };

                const result = { 발코니: [], 일반: [], 공틀: [], 기타: [], 추가항목: [] };
                const allKnown = new Set([...categories.발코니, ...categories.일반, ...categories.공틀, ...categories.기타]);

                // 고정 순서대로 분류
                Object.keys(categories).forEach(category => {
                  categories[category].forEach(item => {
                    if (items.includes(item)) {
                      result[category].push(item);
                    }
                  });
                });

                // 추가 항목
                items.forEach(item => {
                  if (!allKnown.has(item)) {
                    result.추가항목.push(item);
                  }
                });

                return result;
              };

              const categorized = categorizeItems(items);
              const elements = [];

              // 발코니
              if (categorized.발코니.length > 0) {
                elements.push(
                  <optgroup key="발코니" label="─ 발코니 ─">
                    {categorized.발코니.map(item => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </optgroup>
                );
              }

              // 일반
              if (categorized.일반.length > 0) {
                elements.push(
                  <optgroup key="일반" label="─ 일반 ─">
                    {categorized.일반.map(item => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </optgroup>
                );
              }

              // 공틀
              if (categorized.공틀.length > 0) {
                elements.push(
                  <optgroup key="공틀" label="─ 공틀 ─">
                    {categorized.공틀.map(item => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </optgroup>
                );
              }

              // 기타
              if (categorized.기타.length > 0) {
                elements.push(
                  <optgroup key="기타" label="─ 기타 ─">
                    {categorized.기타.map(item => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </optgroup>
                );
              }

              // 추가항목
              if (categorized.추가항목.length > 0) {
                elements.push(
                  <optgroup key="추가항목" label="─ 추가 항목 ─">
                    {categorized.추가항목.map(item => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </optgroup>
                );
              }

              return elements;
            })()}
          </select>
        </div>

        <div className="unit-price-filter-item">
          <label className="unit-price-filter-label">자평 검색</label>
          <input
            type="number"
            value={searchArea}
            onChange={e => {
              setSearchArea(e.target.value);
              setCurrentPage(1);  // 필터 변경 시 1페이지로 이동
            }}
            placeholder="자평 입력"
            className="unit-price-filter-input"
            disabled={!selectedBrand}
          />
        </div>

        <div className="unit-price-item-count">
          총 {filteredPrices.length}개 항목
        </div>
      </div>
      */}

      {/* 브랜드 선택 전 안내 메시지 */}
      {!selectedBrand && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 20px',
            textAlign: 'center',
            background: '#f8f9fa',
            borderRadius: '12px',
            margin: '20px 0',
            border: '2px dashed #dee2e6',
          }}>
          <div
            style={{
              fontSize: '48px',
              color: '#6c757d',
              marginBottom: '16px',
              opacity: 0.6,
            }}>
            📊
          </div>
          <h3
            style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#495057',
              marginBottom: '12px',
              margin: '0 0 12px 0',
            }}>
            브랜드를 선택해주세요
          </h3>
          <p
            style={{
              fontSize: '16px',
              color: '#6c757d',
              marginBottom: '24px',
              lineHeight: '1.5',
              maxWidth: '450px',
            }}>
            위의 <strong>브랜드 선택</strong> 드롭다운에서 확인하고 싶은
            브랜드를 선택하시면
            <br />
            해당 브랜드의 단가 정보가 표시됩니다.
          </p>
        </div>
      )}

      {/* 테이블 2개를 flex로 나란히 배치 */}
      {selectedBrand && (
        <div
          className="unit-price-table-wrap"
          style={{ display: 'flex', gap: '24px', justifyContent: 'center' }}>
          <table
            className="unit-price-table"
            style={{ fontSize: '13px', textAlign: 'center', width: '48%' }}>
            <thead>
              <tr>
                <th>브랜드</th>
                <th>아이템</th>
                <th>자평</th>
                <th>단가</th>
              </tr>
            </thead>
            <tbody>
              {leftPrices.map((price) => (
                <tr key={price.id}>
                  <td>{getBrandDisplayName(price.brand)}</td>
                  <td>{price.item}</td>
                  <td>{price.area}</td>
                  <td className="unit-price-td-price">
                    {price.price.toLocaleString()}원
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <table
            className="unit-price-table"
            style={{ fontSize: '13px', textAlign: 'center', width: '48%' }}>
            <thead>
              <tr>
                <th>브랜드</th>
                <th>아이템</th>
                <th>자평</th>
                <th>단가</th>
              </tr>
            </thead>
            <tbody>
              {rightPrices.map((price) => (
                <tr key={price.id}>
                  <td>{getBrandDisplayName(price.brand)}</td>
                  <td>{price.item}</td>
                  <td>{price.area}</td>
                  <td className="unit-price-td-price">
                    {price.price.toLocaleString()}원
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* 페이지네이션 UI */}
      {selectedBrand && totalPages > 1 && (
        <div
          className="pagination"
          style={{ justifyContent: 'center', marginTop: '10px', gap: '2px' }}>
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ fontSize: '13px', padding: '2px 8px' }}>
            이전
          </button>
          {Array.from({ length: totalPages }, (_, idx) => (
            <button
              key={idx + 1}
              className={currentPage === idx + 1 ? 'active-page' : ''}
              onClick={() => setCurrentPage(idx + 1)}
              style={{ fontSize: '13px', padding: '2px 8px' }}>
              {idx + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ fontSize: '13px', padding: '2px 8px' }}>
            다음
          </button>
        </div>
      )}

      {/* 일괄 단가 수정 모달 */}
      {bulkUpdateModalOpen && (
        <div className="unit-price-modal-overlay">
          <div className="unit-price-modal-box large">
            <div className="unit-price-modal-title-row">
              <h2>일괄 가격수정</h2>
              <button
                className="unit-price-modal-close-btn"
                onClick={handleCloseBulkUpdate}>
                ×
              </button>
            </div>
            <div className="unit-price-modal-desc">
              {/* 상단 한 줄 필터/퍼센트/버튼 */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  margin: '18px 0 18px 0',
                }}>
                <select
                  value={bulkUpdateForm.brand}
                  onChange={async (e) => {
                    const selectedBrand = e.target.value;
                    setBulkUpdateForm((f) => ({
                      ...f,
                      brand: selectedBrand,
                      item: '',
                    }));
                    if (selectedBrand) {
                      await loadBulkItems(selectedBrand);
                    }
                    setPreviewData([]);
                    setShowPreview(false);
                  }}
                  className="unit-price-form-select center-option"
                  style={{ minWidth: '120px', borderRadius: '8px' }}>
                  <option value="">브랜드 선택</option>
                  {brands.map((brand) => (
                    <option key={brand} value={brand}>
                      {getBrandDisplayName(brand)}
                    </option>
                  ))}
                </select>
                <select
                  value={bulkUpdateForm.item}
                  onChange={(e) => {
                    setBulkUpdateForm((f) => ({ ...f, item: e.target.value }));
                    setPreviewData([]);
                    setShowPreview(false);
                  }}
                  disabled={!bulkUpdateForm.brand}
                  className="unit-price-form-select center-option"
                  style={{ minWidth: '120px', borderRadius: '8px' }}>
                  <option value="">전체 아이템</option>
                  {(() => {
                    // 아이템을 카테고리별로 분류
                    const categorizeItems = (items: string[]) => {
                      const categories = {
                        발코니: [
                          '발코니_단창_2W',
                          '발코니_단창_3W',
                          '발코니_이중창_2W',
                          '발코니_이중창_3W',
                        ],
                        일반: [
                          '일반_단창_2W',
                          '일반_단창_3W',
                          '일반_단창_4W',
                          '일반_이중창_2W',
                          '일반_이중창_3W',
                          '일반_이중창_4W',
                        ],
                        공틀: ['공틀_단창_2W', '공틀_단창_3W', '공틀_단창_4W'],
                        기타: ['FIX', '망추가(블랙스텐망)'],
                      };
                      const result: { [k: string]: string[] } = {
                        발코니: [],
                        일반: [],
                        공틀: [],
                        기타: [],
                        추가항목: [],
                      };
                      const allKnown = new Set([
                        ...categories.발코니,
                        ...categories.일반,
                        ...categories.공틀,
                        ...categories.기타,
                      ]);
                      Object.keys(categories).forEach((category) => {
                        categories[category].forEach((item) => {
                          if (items.includes(item)) {
                            result[category].push(item);
                          }
                        });
                      });
                      items.forEach((item) => {
                        if (!allKnown.has(item)) {
                          result.추가항목.push(item);
                        }
                      });
                      return result;
                    };
                    const categorized = categorizeItems(bulkItems);
                    const elements: React.ReactElement[] = [];
                    if (categorized.발코니.length > 0) {
                      elements.push(
                        <optgroup key="발코니" label="─ 발코니 ─">
                          {categorized.발코니.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      );
                    }
                    if (categorized.일반.length > 0) {
                      elements.push(
                        <optgroup key="일반" label="─ 일반 ─">
                          {categorized.일반.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      );
                    }
                    if (categorized.공틀.length > 0) {
                      elements.push(
                        <optgroup key="공틀" label="─ 공틀 ─">
                          {categorized.공틀.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      );
                    }
                    if (categorized.기타.length > 0) {
                      elements.push(
                        <optgroup key="기타" label="─ 기타 ─">
                          {categorized.기타.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      );
                    }
                    if (categorized.추가항목.length > 0) {
                      elements.push(
                        <optgroup key="추가항목" label="─ 추가 항목 ─">
                          {categorized.추가항목.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </optgroup>
                      );
                    }
                    return elements;
                  })()}
                </select>
                {/* 퍼센트 입력 그룹: input 오른쪽에 +, - 버튼(+-순서) */}
                <div className="unit-price-percent-group">
                  <input
                    type="number"
                    className="percent-input"
                    value={bulkUpdateForm.percentage ?? ''}
                    min={-100}
                    max={100}
                    onChange={(e) => {
                      const val = Math.max(
                        -100,
                        Math.min(100, Number(e.target.value ?? 0))
                      );
                      setBulkUpdateForm((f) => ({
                        ...f,
                        percentage: Number(val),
                      }));
                    }}
                    style={{ width: 50, textAlign: 'center', fontWeight: 700 }}
                  />
                  <span style={{ fontWeight: 700, fontSize: 16 }}>%</span>
                  <button
                    type="button"
                    className="percent-btn"
                    onClick={() =>
                      setBulkUpdateForm((f) => ({
                        ...f,
                        percentage: Math.min(100, (f.percentage || 0) + 1),
                      }))
                    }
                    tabIndex={-1}>
                    +
                  </button>
                  <button
                    type="button"
                    className="percent-btn"
                    onClick={() =>
                      setBulkUpdateForm((f) => ({
                        ...f,
                        percentage: Math.max(-100, (f.percentage || 0) - 1),
                      }))
                    }
                    tabIndex={-1}>
                    -
                  </button>
                </div>
                {/* 금액적용 버튼 */}
                <button
                  type="button"
                  className="unit-price-action-btn unit-price-bulk-update-btn apply-btn-center"
                  onClick={handleBulkUpdate}
                  disabled={!previewData.length}
                  style={{
                    minWidth: 100,
                    marginLeft: 12,
                    borderRadius: 8,
                    opacity: previewData.length ? 1 : 0.5,
                    cursor: previewData.length ? 'pointer' : 'not-allowed',
                    fontWeight: 700,
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                  금액적용
                </button>
              </div>
              {/* 자동 미리보기 useEffect */}
              {/* 금액 미리보기 테이블 */}
              <div className="unit-price-preview-container">
                {/* 금액 미리보기 제목 왼쪽 정렬 */}
                <h4 style={{ textAlign: 'left', fontWeight: 700 }}>
                  금액 미리보기{' '}
                  {previewData.length > 0 && (
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: 'normal',
                        color: '#666',
                      }}>
                      (총 {previewData.length}개 항목)
                    </span>
                  )}
                </h4>
                <div className="unit-price-preview-table-container">
                  <table
                    className="unit-price-preview-table"
                    style={{ textAlign: 'center' }}>
                    <thead>
                      <tr style={{ background: '#F0F0F0' }}>
                        <th>브랜드</th>
                        <th>아이템</th>
                        <th>자평</th>
                        <th>기존단가</th>
                        <th>변경단가</th>
                        <th>조정금액</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((item, index) => (
                        <tr key={index} style={{ textAlign: 'center' }}>
                          <td>{getBrandDisplayName(item.brand)}</td>
                          <td>{item.item}</td>
                          <td>{item.area}</td>
                          <td>{item.originalPrice.toLocaleString()}원</td>
                          <td
                            style={{
                              background: '#fffbe7',
                              fontWeight: 'bold',
                              color: '#b48a00',
                            }}>
                            {item.newPrice.toLocaleString()}원
                          </td>
                          <td style={{ color: '#e74c3c', fontWeight: 700 }}>
                            {item.newPrice - item.originalPrice > 0 ? '+' : ''}
                            {(
                              item.newPrice - item.originalPrice
                            ).toLocaleString()}
                            원
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 편집 모달 */}
      {editModalOpen && (
        <div className="unit-price-modal-overlay">
          <div className="unit-price-modal-box">
            <div className="unit-price-modal-title-row">
              <h2>💰 단가 수정</h2>
              <button
                className="unit-price-modal-close-btn"
                onClick={() => setEditModalOpen(false)}>
                ×
              </button>
            </div>
            <div className="unit-price-info-box">
              ℹ️ <strong>안내:</strong> 보안상의 이유로 브랜드, 아이템, 자평은
              수정할 수 없으며, 단가만 변경 가능합니다.
            </div>
            <div className="unit-price-modal-desc">
              <div className="unit-price-edit-form-row">
                <label>브랜드</label>
                <input
                  type="text"
                  value={getBrandDisplayName(editForm.brand)}
                  readOnly
                  className="unit-price-form-input readonly"
                />
                <small className="unit-price-form-hint">
                  * 브랜드는 수정할 수 없습니다.
                </small>
              </div>

              <div className="unit-price-edit-form-row">
                <label>아이템</label>
                <input
                  type="text"
                  value={editForm.item}
                  readOnly
                  className="unit-price-form-input readonly"
                />
                <small className="unit-price-form-hint">
                  * 아이템은 수정할 수 없습니다.
                </small>
              </div>

              <div className="unit-price-edit-form-row">
                <label>자평</label>
                <input
                  type="number"
                  value={editForm.area}
                  readOnly
                  className="unit-price-form-input readonly"
                />
                <small className="unit-price-form-hint">
                  * 자평은 수정할 수 없습니다.
                </small>
              </div>

              <div className="unit-price-edit-form-row">
                <label>
                  단가 <span style={{ color: '#007bff' }}>*</span>
                </label>
                <input
                  type="number"
                  value={editForm.price}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, price: e.target.value }))
                  }
                  placeholder="단가를 입력하세요"
                  className="unit-price-form-input editable"
                />
                {formError.price && (
                  <div className="unit-price-form-error">{formError.price}</div>
                )}
                <small className="unit-price-form-hint primary">
                  * 단가만 수정 가능합니다.
                </small>
              </div>

              <div className="unit-price-modal-btn-row">
                <button
                  type="button"
                  className="unit-price-modal-cancel-btn"
                  onClick={() => setEditModalOpen(false)}>
                  취소
                </button>
                <button
                  type="button"
                  className="unit-price-modal-confirm-btn"
                  onClick={handleSaveEdit}>
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 추가 모달 */}
      {addModalOpen && (
        <div className="unit-price-modal-overlay">
          <div className="unit-price-modal-box">
            <div className="unit-price-modal-title-row">
              <h2>가격 추가</h2>
              <button
                className="unit-price-modal-close-btn"
                onClick={() => {
                  setAddModalOpen(false);
                  setAddForm({
                    brand: '',
                    item: '',
                    area: '',
                    price: '',
                    customItem: '',
                  });
                }}>
                ×
              </button>
            </div>
            <div className="unit-price-modal-desc">
              <div className="unit-price-edit-form-row">
                <label>브랜드</label>
                <select
                  value={addForm.brand}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, brand: e.target.value }))
                  }
                  className="unit-price-form-select">
                  <option value="">선택하세요</option>
                  {brands.map((brand) => (
                    <option key={brand} value={brand}>
                      {getBrandDisplayName(brand)}
                    </option>
                  ))}
                </select>
                {formError.brand && (
                  <div className="unit-price-form-error">{formError.brand}</div>
                )}
              </div>

              <div className="unit-price-edit-form-row">
                <label>아이템</label>
                {addForm.item === '직접입력' ? (
                  <input
                    type="text"
                    value={addForm.customItem || ''}
                    onChange={(e) =>
                      setAddForm((f) => ({ ...f, customItem: e.target.value }))
                    }
                    placeholder="새 아이템명을 입력하세요 (예: 발코니_단창_2W)"
                    className="unit-price-form-input"
                  />
                ) : (
                  <select
                    value={addForm.item}
                    onChange={(e) =>
                      setAddForm((f) => ({
                        ...f,
                        item: e.target.value,
                        customItem: '',
                      }))
                    }
                    className="unit-price-form-select">
                    <option value="">선택하세요</option>
                    <optgroup label="─ 발코니 ─">
                      <option value="발코니_단창_2W">발코니_단창_2W</option>
                      <option value="발코니_단창_3W">발코니_단창_3W</option>
                      <option value="발코니_이중창_2W">발코니_이중창_2W</option>
                      <option value="발코니_이중창_3W">발코니_이중창_3W</option>
                    </optgroup>
                    <optgroup label="─ 일반 ─">
                      <option value="일반_단창_2W">일반_단창_2W</option>
                      <option value="일반_단창_3W">일반_단창_3W</option>
                      <option value="일반_단창_4W">일반_단창_4W</option>
                      <option value="일반_이중창_2W">일반_이중창_2W</option>
                      <option value="일반_이중창_3W">일반_이중창_3W</option>
                      <option value="일반_이중창_4W">일반_이중창_4W</option>
                    </optgroup>
                    <optgroup label="─ 공틀 ─">
                      <option value="공틀_단창_2W">공틀_단창_2W</option>
                      <option value="공틀_단창_3W">공틀_단창_3W</option>
                      <option value="공틀_단창_4W">공틀_단창_4W</option>
                    </optgroup>
                    <optgroup label="─ 기타 ─">
                      <option value="FIX">FIX</option>
                      <option value="망추가(블랙스텐망)">망추가(블랙스텐망)</option>

                    </optgroup>
                    <optgroup label="─ 직접 입력 ─">
                      <option value="직접입력">직접입력</option>
                    </optgroup>
                  </select>
                )}
                {addForm.item === '직접입력' && (
                  <button
                    type="button"
                    onClick={() =>
                      setAddForm((f) => ({ ...f, item: '', customItem: '' }))
                    }
                    className="unit-price-direct-input-cancel">
                    선택 취소
                  </button>
                )}
                {formError.item && (
                  <div className="unit-price-form-error">{formError.item}</div>
                )}
                <small className="unit-price-form-hint">
                  {addForm.item === '직접입력'
                    ? '* 새로운 아이템명을 입력해주세요.'
                    : "* '직접입력' 선택 시 새로운 아이템을 추가할 수 있습니다."}
                </small>
              </div>

              <div className="unit-price-edit-form-row">
                <label>자평</label>
                <input
                  type="number"
                  value={addForm.area}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, area: e.target.value }))
                  }
                  placeholder="1~200"
                  className="unit-price-form-input"
                />
                {formError.area && (
                  <div className="unit-price-form-error">{formError.area}</div>
                )}
              </div>

              <div className="unit-price-edit-form-row">
                <label>단가</label>
                <input
                  type="number"
                  value={addForm.price}
                  onChange={(e) =>
                    setAddForm((f) => ({ ...f, price: e.target.value }))
                  }
                  placeholder="원"
                  className="unit-price-form-input"
                />
                {formError.price && (
                  <div className="unit-price-form-error">{formError.price}</div>
                )}
              </div>

              <div className="unit-price-modal-btn-row">
                <button
                  type="button"
                  className="unit-price-modal-cancel-btn"
                  onClick={() => {
                    setAddModalOpen(false);
                    setAddForm({
                      brand: '',
                      item: '',
                      area: '',
                      price: '',
                      customItem: '',
                    });
                  }}>
                  취소
                </button>
                {/*<button type="button" className="unit-price-modal-confirm-btn" onClick={handleSaveAdd}>
                  추가
                </button> */}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UnitPriceManagement;
